import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTournaments, deleteTournament } from '../../store/slices/tournamentSlice';
import { useParams, Link } from 'react-router-dom';
import {
    Plus,
    Calendar,
    MapPin,
    DollarSign,
    Users,
    Edit3,
    Trash2,
    Trophy,
    ArrowRight,
    ChevronRight
} from 'lucide-react';

const AdminTournaments = () => {
    const dispatch = useDispatch();
    const { tournaments, loading, error } = useSelector((state) => state.tournaments);
    const { slug } = useParams();

    useEffect(() => {
        dispatch(fetchTournaments(slug));
    }, [dispatch, slug]);

    const handleDelete = (id) => {
        if (window.confirm('¿Estás seguro de eliminar este torneo?')) {
            dispatch(deleteTournament({ slug, id }));
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-6 rounded-[32px] max-w-md text-center">
                    <p className="text-lg font-black mb-2">Error al cargar torneos</p>
                    <p className="text-sm font-medium opacity-60">
                        {typeof error === 'object' ? error.message || JSON.stringify(error) : error}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20">
            {/* HERO / HEADER */}
            <div className="relative pt-12 pb-24 px-6 border-b border-white/5 bg-white/[0.01]">
                <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] animate-glow" />
                </div>

                <div className="mx-auto max-w-7xl relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full mb-4">
                            <Trophy size={12} className="text-blue-500" />
                            <span className="text-[10px] font-black uppercase tracking-[2px] text-white/60">Organizador</span>
                        </div>
                        <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-2 uppercase">Gestión de <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600 leading-relaxed">Torneos</span></h1>
                        <p className="text-white/40 font-medium tracking-tight">Administrá escuadras, fixture y resultados de tu competencia.</p>
                    </div>

                    <Link
                        to={`/${slug}/admin/tournaments/create`}
                        className="group bg-gradient-to-r from-blue-600 to-blue-400 px-8 py-5 rounded-[24px] font-black text-white shadow-xl shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                    >
                        <Plus size={24} />
                        CREAR TORNEO
                        <ChevronRight className="opacity-40 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </div>

            {/* GRID */}
            <div className="mx-auto max-w-7xl px-6 -mt-12 relative z-10">
                {!tournaments || tournaments.length === 0 ? (
                    <div className="bg-glass p-20 rounded-[48px] text-center border-dashed flex flex-col items-center">
                        <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center text-white/10 mb-6 font-black italic text-3xl">?</div>
                        <p className="text-white/20 font-black uppercase tracking-widest text-sm mb-8">Aún no creaste ningún torneo</p>
                        <Link
                            to={`/${slug}/admin/tournaments/create`}
                            className="text-blue-400 font-black flex items-center gap-2 hover:gap-4 transition-all"
                        >
                            EMPEZÁ AHORA <ArrowRight size={20} />
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-20">
                        <section>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {tournaments.map((tournament) => (
                                    <AdminTournamentCard
                                        key={tournament._id}
                                        tournament={tournament}
                                        slug={slug}
                                        handleDelete={handleDelete}
                                    />
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdminTournamentCard = ({ tournament, slug, handleDelete }) => {
    const isPast = tournament.status === 'Finalizado';

    return (
        <div
            className={`bg-glass rounded-[40px] shadow-3xl overflow-hidden group hover:border-white/20 transition-all duration-500 shine-effect flex flex-col ${isPast ? 'grayscale-[0.5]' : ''}`}
        >
            {/* Portada */}
            <div className="h-48 relative overflow-hidden bg-white/5 border-b border-white/5">
                {tournament.coverUrl ? (
                    <img
                        src={tournament.coverUrl}
                        alt={tournament.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-10">
                        <Trophy size={64} />
                    </div>
                )}
                <div className="absolute top-4 left-4 flex gap-2">
                    <div className={`px-3 py-1.5 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10 text-white shadow-lg ${tournament.status === 'En Curso' ? 'bg-green-500/80 shadow-green-500/20' :
                        tournament.status === 'Finalizado' ? 'bg-zinc-500/80' : 'bg-blue-500/80 shadow-blue-500/20'
                        }`}>
                        {tournament.status}
                    </div>
                    {tournament.isPublic === false && (
                        <div className="px-3 py-1.5 bg-black/80 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10 text-white">
                            Privado
                        </div>
                    )}
                </div>
            </div>

            <div className="p-8 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-4">
                    <h3 className={`text-2xl font-black group-hover:text-blue-400 transition-colors line-clamp-2 leading-tight ${isPast ? 'text-white/60' : ''}`}>
                        {tournament.name}
                    </h3>
                    <span className="px-2 py-1 bg-white/10 rounded-md text-[10px] uppercase font-bold text-white/50">{tournament.sport}</span>
                </div>

                <div className="space-y-4 mb-8">
                    <TournamentMeta icon={<Calendar size={14} />} label="Inicio" value={tournament.startDate ? new Date(tournament.startDate).toLocaleDateString('es-AR', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' }) : 'TBD'} />
                    <TournamentMeta icon={<DollarSign size={14} />} label="Inscripción" value={`$${tournament.registrationFee} x eq.`} />
                    <TournamentMeta icon={<MapPin size={14} />} label="Sede" value={tournament.location || 'No definida'} />
                    <TournamentMeta icon={<Users size={14} />} label="Equipos" value={`${tournament.enrolledTeams} / ${tournament.maxTeams}`} />
                </div>

                {/* Acciones */}
                <div className="mt-auto grid grid-cols-2 gap-3">
                    <Link
                        to={`/${slug}/admin/tournaments/${tournament._id}/teams`}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-2xl text-[10px] md:text-sm font-black text-blue-400 hover:bg-blue-500 hover:text-white transition-all"
                    >
                        <Users size={16} /> EQUIPOS
                    </Link>

                    <Link
                        to={`/${slug}/admin/tournaments/${tournament._id}/fixture`}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/10 border border-purple-500/20 rounded-2xl text-[10px] md:text-sm font-black text-purple-400 hover:bg-purple-500 hover:text-white transition-all"
                    >
                        <Calendar size={16} /> FIXTURE
                    </Link>

                    <Link
                        to={`/${slug}/admin/tournaments/edit/${tournament._id}`}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-white/5 rounded-2xl text-sm font-black text-white hover:bg-white hover:text-black transition-all"
                    >
                        <Edit3 size={16} /> EDITAR
                    </Link>
                    <button
                        onClick={() => handleDelete(tournament._id)}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-2xl text-sm font-black text-red-400 hover:bg-red-500 hover:text-white transition-all"
                    >
                        <Trash2 size={16} /> BORRAR
                    </button>
                </div>
            </div>
        </div >
    );
};

const TournamentMeta = ({ icon, label, value }) => (
    <div className="flex items-center gap-3">
        <div className="text-blue-500/50">{icon}</div>
        <div className="flex flex-col">
            <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px] leading-none mb-1">{label}</p>
            <p className="text-sm font-bold text-white/80 leading-none">{value}</p>
        </div>
    </div>
);

export default AdminTournaments;
